import type { ComponentProps, ReactElement } from "react";
import { memo } from "react";

import { useSelector } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";

import {
    ColumnsSelectionButtonContainer,
    ColumnsSelectionContainer,
    type TableColumnsSelection,
    type TriggerProps,
} from "app/pages/sales-intelligence/common-components/column-picker";
import { selectActiveLeadsType } from "app/pages/sales-intelligence/modules/leads-search";
import { getLeadsSearchColumnListConfig } from "app/pages/sales-intelligence/modules/leads-search/configuration/columns/columnsConfig";
import type { SuggestionsApplyFunc } from "app/pages/sales-intelligence/modules/leads-search/hooks/useFilterColumnSuggestions";
import useFilterColumnSuggestions from "app/pages/sales-intelligence/modules/leads-search/hooks/useFilterColumnSuggestions";
import { useLeadsColumnSelectionOptions } from "../../../hooks/useLeadsColumnSelectionOptions";
import { ColumnSuggestionsView } from "app/pages/sales-intelligence/sub-modules/column-suggestions/components/ColumnSuggestionsView";

type Props = {
    isDisabled: boolean;
    onSuggestionsApply: SuggestionsApplyFunc;
} & Pick<ComponentProps<typeof TableColumnsSelection>, "renderSelectionModal">;

const TableColumnsSelectionContainer = ({
    isDisabled,
    onSuggestionsApply,
    renderSelectionModal,
}: Props): ReactElement => {
    const { translate } = useTranslate();

    const activeLeadsType = useSelector(selectActiveLeadsType);
    const columnListConfig = getLeadsSearchColumnListConfig();
    const { selectedColumnIds, availableColumns } = useLeadsColumnSelectionOptions(columnListConfig, activeLeadsType);

    const [filterColumnSuggestions, handleSuggestionsApply] = useFilterColumnSuggestions(
        onSuggestionsApply,
        selectedColumnIds,
        availableColumns,
        columnListConfig,
    );

    const renderSelectionTrigger = ({ onClick }: TriggerProps): ReactElement => {
        if (filterColumnSuggestions.length === 0) {
            return (
                <ColumnsSelectionButtonContainer
                    isDisabled={isDisabled}
                    suggestionsCount={filterColumnSuggestions.length}
                    tooltipContent={translate("si.columns-selection.tooltip.change-columns")}
                    onClick={onClick}
                />
            );
        }

        return (
            <ColumnSuggestionsView
                columnSuggestions={filterColumnSuggestions}
                isTriggerDisabled={isDisabled}
                renderTrigger={({ isViewOpen, onViewToggle }) => (
                    <ColumnsSelectionButtonContainer
                        isDisabled={isDisabled}
                        suggestionsCount={filterColumnSuggestions.length}
                        tooltipContent={translate("si.columns-selection.tooltip.view-suggestions")}
                        withTooltip={!isViewOpen}
                        onClick={onViewToggle}
                    />
                )}
                onApplyClick={handleSuggestionsApply}
                onCancelClick={onClick}
            />
        );
    };

    return (
        <ColumnsSelectionContainer
            renderSelectionTrigger={renderSelectionTrigger}
            renderSelectionModal={renderSelectionModal}
        />
    );
};

export default memo(TableColumnsSelectionContainer);
